import * as XLSX from 'xlsx';
import moment from 'moment';
import { getDebtExcelLabels, getDetailExcelLabels } from './reportExcelI18n';

/** Server ya envía el name del catálogo (ej. "Pago por un tercero"). */
const formatNominasLabelForExcel = (value) => {
  if (value == null || value === '' || value === 'NO_LABEL' || value === 'sinetiqueta') return '';
  return String(value);
};

/**
 * Exports data to Excel file (.xlsx)
 * @param {Object} options - Export options
 * @param {Array} options.rows - Array of data objects to export
 * @param {Array} options.columns - Optional array of column definitions { key, label }
 * @param {string} options.fileName - Optional filename (default: 'export-YYYY-MM-DD.xlsx')
 * @param {string} options.sheetName - Optional sheet name (default: 'Datos')
 * @returns {boolean} - Returns true if export was successful
 */
export const exportToExcel = ({ 
  rows = [], 
  columns = null, 
  fileName = null, 
  sheetName = 'Datos' 
}) => {
  try {
    // Validate input
    if (!rows || rows.length === 0) {
      console.warn('No data to export');
      return false;
    }

    // Prepare data for export
    let exportData = [];
    
    if (columns && columns.length > 0) {
      // Use defined columns
      exportData = rows.map(row => {
        const newRow = {};
        columns.forEach(col => {
          const label = col.label || col.key;
          const value = getNestedValue(row, col.key);
          newRow[label] = formatValue(value);
        });
        return newRow;
      });
    } else {
      // Auto-generate columns from first object
      exportData = rows.map(row => {
        const newRow = {};
        Object.keys(row).forEach(key => {
          // Skip nested objects and arrays
          if (typeof row[key] !== 'object' || row[key] === null) {
            newRow[key] = formatValue(row[key]);
          }
        });
        return newRow;
      });
    }

    // Create workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Auto-size columns
    const maxWidth = 50;
    const columnWidths = [];
    
    if (exportData.length > 0) {
      const firstRow = exportData[0];
      Object.keys(firstRow).forEach((key, index) => {
        const headerLength = key.length;
        const maxContentLength = exportData.reduce((max, row) => {
          const cellValue = String(row[key] || '');
          return Math.max(max, cellValue.length);
        }, headerLength);
        columnWidths[index] = { wch: Math.min(maxContentLength + 2, maxWidth) };
      });
      worksheet['!cols'] = columnWidths;
    }

    // Generate filename
    const defaultFileName = `export-${moment().format('YYYY-MM-DD')}.xlsx`;
    const finalFileName = fileName || defaultFileName;

    // Download file
    XLSX.writeFile(workbook, finalFileName);
    
    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return false;
  }
};

/**
 * Gets nested value from object using dot notation
 * @param {Object} obj - Source object
 * @param {string} path - Path to value (e.g., 'client.name')
 * @returns {*} - The value at the path
 */
const getNestedValue = (obj, path) => {
  if (!path) return obj;
  
  return path.split('.').reduce((current, key) => {
    return current?.[key];
  }, obj);
};

/**
 * Formats value for Excel export
 * @param {*} value - Value to format
 * @returns {string|number|null} - Formatted value
 */
const formatValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  
  // Handle dates
  if (value instanceof Date) {
    return moment(value).format('DD/MM/YYYY');
  }
  
  // Handle moment objects
  if (moment.isMoment(value)) {
    return value.format('DD/MM/YYYY');
  }
  
  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 'Sí' : 'No';
  }
  
  // Return as is for numbers and strings
  return value;
};

/**
 * Export rendiciones/obligations data to Excel
 * @param {Object} data - Document data with glosa/detail information
 * @param {string} documentType - Type of document for filename
 */
export const exportRendicionesToExcel = (data, documentType = 'documento') => {
  if (!data) {
    console.warn('No data provided for export');
    return false;
  }

  // Try to get glosa items from different possible locations
  const glosaItems = data.meta?.description || data.meta?.detail || data.meta?.items || [];
  
  if (!glosaItems || glosaItems.length === 0) {
    console.warn('No items to export');
    return false;
  }

  // Filter out empty items
  const filteredItems = glosaItems.filter(item => {
    const hasDescription = item.description || item.name || item.item;
    const hasUnitPrice = item.unit_price && item.unit_price !== 0;
    const hasQuantity = item.quantity && item.quantity !== 0;
    const hasTotalPrice = item.total_price && item.total_price !== 0;
    return hasDescription || hasUnitPrice || hasQuantity || hasTotalPrice;
  });

  if (filteredItems.length === 0) {
    console.warn('No valid items to export after filtering');
    return false;
  }

  // Define columns for rendiciones
  const columns = [
    { key: 'description', label: 'GLOSA' },
    { key: 'unit_price', label: 'UNITARIO' },
    { key: 'quantity', label: 'CANTIDAD' },
    { key: 'total_price', label: 'TOTAL' }
  ];

  // Prepare rows with combined description
  const rows = filteredItems.map(item => ({
    description: item.name && item.description
      ? `${item.name} ${item.description}`
      : item.name || item.description || item.item || 'Sin glosa ingresada',
    unit_price: item.unit_price || 0,
    quantity: item.quantity || 1,
    total_price: item.total_price || (item.unit_price * (item.quantity || 1)) || 0
  }));

  // Generate filename
  const docNumber = data.meta?.documentId || data.meta?.number || 'sin-numero';
  const date = moment(data.documentDate).format('YYYY-MM-DD');
  const fileName = `rendiciones-${documentType}-${docNumber}-${date}.xlsx`;

  return exportToExcel({
    rows,
    columns,
    fileName,
    sheetName: 'glosa'
  });
};

/**
 * Exporta reporte de ventas/compras a Excel con formato profesional de consultoría
 * Genera 5 hojas: Portada, Resumen Mensual, Clientes/Proveedores, Detalle, Datos
 * @param {Object} options - Opciones de exportación
 * @param {Array} options.entities - Array de entidades con sus datos mensuales y facturas
 * @param {Array} options.months - Array de nombres de meses
 * @param {Array} options.totals - Array de totales por mes
 * @param {number} options.grandTotal - Total general
 * @param {string} options.entityLabel - Etiqueta de la entidad (Cliente/Proveedor)
 * @param {string} options.fileName - Nombre del archivo
 * @param {string} options.reportTitle - Título del reporte
 * @param {number} options.selectedYear - Año seleccionado
 * @param {string} options.companyName - Nombre de la empresa (opcional)
 * @returns {boolean} - true si la exportación fue exitosa
 */
export const exportDetailReportToExcel = ({
  entities = [],
  months = [],
  totals = [],
  grandTotal = 0,
  entityLabel = 'Cliente',
  fileName = 'reporte-ventas.xlsx',
  reportTitle = 'Reporte de Ventas',
  selectedYear = new Date().getFullYear(),
  companyName = ''
}) => {
  try {
    if (!entities || entities.length === 0) {
      console.warn('No hay datos para exportar');
      return false;
    }

    const workbook = XLSX.utils.book_new();
    const mode = entityLabel === 'Proveedor' ? 'purchases' : 'sales';
    const xl = getDetailExcelLabels(entityLabel, mode);

    const resumenMensual = [];
    
    months.forEach((month, index) => {
      const ventasMes = totals[index] || 0;
      
      resumenMensual.push({
        [xl.month]: month,
        [xl.year]: selectedYear,
        [xl.periodTotal]: ventasMes,
        '': '',
        [xl.grandTotal]: index === 0 ? grandTotal : ''
      });
    });
    
    const wsResumenMensual = XLSX.utils.json_to_sheet(resumenMensual);
    wsResumenMensual['!cols'] = [
      { wch: 12 }, // Mes
      { wch: 8 },  // Año
      { wch: 18 }, // Ventas del Mes
      { wch: 3 },  // Separador
      { wch: 18 }  // Total
    ];
    
    // Aplicar formatos
    const rangeRM = XLSX.utils.decode_range(wsResumenMensual['!ref'] || 'A1');
    for (let R = rangeRM.s.r + 1; R <= rangeRM.e.r; ++R) {
      // Ventas del Mes (C)
      const ventasCell = wsResumenMensual[XLSX.utils.encode_cell({ r: R, c: 2 })];
      if (ventasCell && typeof ventasCell.v === 'number') ventasCell.z = '#,##0';
      // TOTAL VENTAS (E)
      const totalVentasCell = wsResumenMensual[XLSX.utils.encode_cell({ r: R, c: 4 })];
      if (totalVentasCell && typeof totalVentasCell.v === 'number') totalVentasCell.z = '#,##0';
    }
    
    // Autofilter solo en columnas de datos (A-C)
    wsResumenMensual['!autofilter'] = { ref: `A1:C${resumenMensual.length + 1}` };
    XLSX.utils.book_append_sheet(workbook, wsResumenMensual, xl.sheetMonthlySummary);

    // ========================================================================
    // PASO 4: HOJA "Clientes/Proveedores" - Ranking con métricas
    // ========================================================================
    // Ordenar por ventas totales (mayor a menor)
    const entitiesSorted = [...entities].sort((a, b) => (b.total || 0) - (a.total || 0));
    
    // Calcular totales primero - usar la suma real de las entidades para consistencia
    const totalVentas = entities.reduce((sum, e) => sum + (e.total || 0), 0);
    const totalNumVentas = entities.reduce((sum, entity) => {
      let numVentas = 0;
      months.forEach(month => {
        const facturas = entity.facturas?.[month] || [];
        numVentas += facturas.length || (entity.monthlyData?.[month] > 0 ? 1 : 0);
      });
      return sum + numVentas;
    }, 0);
    
    const clientesData = entitiesSorted.map((entity, index) => {
      // Contar número de ventas
      let numVentas = 0;
      months.forEach(month => {
        const facturas = entity.facturas?.[month] || [];
        numVentas += facturas.length || (entity.monthlyData?.[month] > 0 ? 1 : 0);
      });
      
      // Usar el porcentaje pre-calculado con el método Largest Remainder (viene del componente)
      // Si no existe, calcularlo normalmente
      const porcentaje = entity.percentage !== undefined 
        ? entity.percentage 
        : (totalVentas > 0 ? Number(((entity.total / totalVentas) * 100).toFixed(1)) : 0);
      
      // Solo la primera fila tiene los totales en columnas separadas
      const row = {
        [xl.ranking]: index + 1,
        [entityLabel]: entity.entityName || '',
        [xl.entityTotal]: entity.total || 0,
        [xl.percentOfTotal]: porcentaje,
        [xl.transactionCount]: numVentas,
        '': ''
      };
      
      if (index === 0) {
        row[xl.grandTotal] = totalVentas;
        row[xl.totalPercent] = '100%';
        row[xl.totalCount] = totalNumVentas;
      } else {
        row[xl.grandTotal] = '';
        row[xl.totalPercent] = '';
        row[xl.totalCount] = '';
      }
      
      return row;
    });
    
    const wsClientes = XLSX.utils.json_to_sheet(clientesData);
    wsClientes['!cols'] = [
      { wch: 8 },  // Ranking
      { wch: 45 }, // Entidad
      { wch: 18 }, // Ventas Totales
      { wch: 12 }, // % del Total
      { wch: 16 }, // Número de Ventas
      { wch: 3 },  // Separador
      { wch: 18 }, // TOTAL VENTAS
      { wch: 10 }, // TOTAL %
      { wch: 16 }  // TOTAL Nº VENTAS
    ];
    
    // Aplicar formatos
    const rangeCli = XLSX.utils.decode_range(wsClientes['!ref'] || 'A1');
    for (let R = rangeCli.s.r + 1; R <= rangeCli.e.r; ++R) {
      // Ventas Totales (C)
      const ventasCell = wsClientes[XLSX.utils.encode_cell({ r: R, c: 2 })];
      if (ventasCell && typeof ventasCell.v === 'number') ventasCell.z = '#,##0';
      // % del Total (D)
      const pctCell = wsClientes[XLSX.utils.encode_cell({ r: R, c: 3 })];
      if (pctCell && typeof pctCell.v === 'number') pctCell.z = '0.0"%"';
      // TOTAL VENTAS (G)
      const totalVentasCell = wsClientes[XLSX.utils.encode_cell({ r: R, c: 6 })];
      if (totalVentasCell && typeof totalVentasCell.v === 'number') totalVentasCell.z = '#,##0';
    }
    
    // Autofilter solo en columnas de datos (A-E)
    wsClientes['!autofilter'] = { ref: `A1:E${clientesData.length + 1}` };
    XLSX.utils.book_append_sheet(workbook, wsClientes, xl.sheetEntities);

    // ========================================================================
    // PASO 3: HOJA "Detalle por Mes" - Formato plano para análisis
    // ========================================================================
    const detalleMensualData = [];
    
    entities.forEach(entity => {
      months.forEach(month => {
        const monthAmount = entity.monthlyData?.[month] || 0;
        if (monthAmount > 0) {
          detalleMensualData.push({
            [entityLabel]: entity.entityName || '',
            [xl.month]: month,
            [xl.year]: selectedYear,
            [xl.amount]: monthAmount
          });
        }
      });
    });
    
    const wsDetalleMensual = XLSX.utils.json_to_sheet(detalleMensualData);
    wsDetalleMensual['!cols'] = [
      { wch: 45 }, // Entidad
      { wch: 10 }, // Mes
      { wch: 8 },  // Año
      { wch: 18 }  // Monto
    ];
    
    // Formato moneda
    const rangeDM = XLSX.utils.decode_range(wsDetalleMensual['!ref'] || 'A1');
    for (let R = rangeDM.s.r + 1; R <= rangeDM.e.r; ++R) {
      const montoCell = wsDetalleMensual[XLSX.utils.encode_cell({ r: R, c: 3 })];
      if (montoCell && typeof montoCell.v === 'number') montoCell.z = '#,##0';
    }
    
    wsDetalleMensual['!autofilter'] = { ref: wsDetalleMensual['!ref'] };
    XLSX.utils.book_append_sheet(workbook, wsDetalleMensual, xl.sheetDetailByMonth);

    // ========================================================================
    // PASO 4: HOJA "Detalle" - Anexo técnico con filtros
    // ========================================================================
    const detalleData = [];
    
    entities.forEach(entity => {
      months.forEach(month => {
        const facturas = entity.facturas?.[month] || [];
        
        if (facturas.length > 0) {
          facturas.forEach(factura => {
            detalleData.push({
              [entityLabel]: entity.entityName || '',
              [xl.folio]: factura.documentId || '',
              [xl.date]: factura.issuedAt ? moment(factura.issuedAt).format('DD-MM-YYYY') : '',
              [xl.amount]: factura.netAmount || 0
            });
          });
        } else {
          const monthAmount = entity.monthlyData?.[month] || 0;
          if (monthAmount > 0) {
            detalleData.push({
              [entityLabel]: entity.entityName || '',
              [xl.folio]: '',
              [xl.date]: '',
              [xl.amount]: monthAmount
            });
          }
        }
      });
    });
    
    const wsDetalle = XLSX.utils.json_to_sheet(detalleData);
    wsDetalle['!cols'] = [
      { wch: 45 }, // Entidad
      { wch: 12 }, // Folio
      { wch: 12 }, // Fecha
      { wch: 18 }  // Monto
    ];
    
    // Formato moneda
    const rangeDet = XLSX.utils.decode_range(wsDetalle['!ref'] || 'A1');
    for (let R = rangeDet.s.r + 1; R <= rangeDet.e.r; ++R) {
      const montoCell = wsDetalle[XLSX.utils.encode_cell({ r: R, c: 3 })];
      if (montoCell && typeof montoCell.v === 'number') montoCell.z = '#,##0';
    }
    
    wsDetalle['!autofilter'] = { ref: wsDetalle['!ref'] };
    XLSX.utils.book_append_sheet(workbook, wsDetalle, 'Detalle');

    // ========================================================================
    // REORDENAR HOJAS
    // ========================================================================
    // Nombre de la hoja: "proveedor" en minúsculas cuando es Proveedor, "clientes" cuando es Cliente
    workbook.SheetNames = [xl.sheetMonthlySummary, xl.sheetEntities, xl.sheetDetailByMonth, xl.sheetDetail];

    // Descargar archivo
    XLSX.writeFile(workbook, fileName);
    
    return true;
  } catch (error) {
    console.error('Error al exportar a Excel:', error);
    return false;
  }
};

/**
 * Exporta reporte de deuda (cobranza/proveedores) a Excel con formato profesional de consultoría
 * Genera 5 hojas: Portada, Resumen por Período, Clientes/Proveedores, Detalle, Datos
 * @param {Object} options - Opciones de exportación
 * @param {Array} options.tableData - Datos de la tabla (proveedores/clientes)
 * @param {Object} options.documentsByEntity - Documentos agrupados por entidad
 * @param {Object} options.totals - Totales por período
 * @param {string} options.entityLabel - Etiqueta de la entidad
 * @param {string} options.fileName - Nombre del archivo
 * @param {string} options.reportTitle - Título del reporte
 * @param {string} options.companyName - Nombre de la empresa (opcional)
 * @returns {boolean} - true si la exportación fue exitosa
 */
export const exportDebtReportToExcel = ({
  tableData = [],
  documentsByEntity = {},
  totals = {},
  entityLabel = 'Cliente',
  fileName = 'reporte-deuda.xlsx',
  reportTitle = 'Reporte de Cobranza',
  companyName = ''
}) => {
  try {
    if (!tableData || tableData.length === 0) {
      console.warn('No hay datos para exportar');
      return false;
    }

    const workbook = XLSX.utils.book_new();
    const xl = getDebtExcelLabels(entityLabel);

    // ========================================================================
    // PASO 1: HOJA "Resumen por Período" - Antigüedad de la deuda
    // ========================================================================
    const totalDeuda = totals.total || 0;
    const deudaVigente = totals.vigente || 0;
    const _deudaVencida = totalDeuda - deudaVigente;
    const bucketTotals = [
      totals.vigente,
      totals.d1_30,
      totals.d31_60,
      totals.d61_90,
      totals.d91_120,
      totals.d120p,
    ];
    const resumenPeriodo = xl.agingBuckets.map(({ periodo }, i) => {
      const monto = bucketTotals[i] || 0;
      const row = {
        [xl.period]: periodo,
        [xl.amount]: monto,
        [xl.percentOfTotal]: totalDeuda > 0 ? Number(((monto / totalDeuda) * 100).toFixed(1)) : 0,
      };
      if (i === 0) {
        row[''] = '';
        row[xl.totalDebt] = totalDeuda;
      }
      return row;
    });
    
    const wsResumenPeriodo = XLSX.utils.json_to_sheet(resumenPeriodo);
    wsResumenPeriodo['!cols'] = [
      { wch: 15 }, // Período
      { wch: 18 }, // Monto
      { wch: 12 }, // % del Total
      { wch: 3 },  // Separador
      { wch: 18 }  // TOTAL DEUDA
    ];
    
    // Aplicar formatos
    const rangeRP = XLSX.utils.decode_range(wsResumenPeriodo['!ref'] || 'A1');
    for (let R = rangeRP.s.r + 1; R <= rangeRP.e.r; ++R) {
      const montoCell = wsResumenPeriodo[XLSX.utils.encode_cell({ r: R, c: 1 })];
      if (montoCell && typeof montoCell.v === 'number') montoCell.z = '#,##0';
      const pctCell = wsResumenPeriodo[XLSX.utils.encode_cell({ r: R, c: 2 })];
      if (pctCell && typeof pctCell.v === 'number') pctCell.z = '0.0"%"';
      // TOTAL DEUDA (E)
      const totalDeudaCell = wsResumenPeriodo[XLSX.utils.encode_cell({ r: R, c: 4 })];
      if (totalDeudaCell && typeof totalDeudaCell.v === 'number') totalDeudaCell.z = '#,##0';
    }
    
    // Autofilter solo en columnas de datos (A-C)
    wsResumenPeriodo['!autofilter'] = { ref: `A1:C${resumenPeriodo.length + 1}` };
    XLSX.utils.book_append_sheet(workbook, wsResumenPeriodo, xl.sheetSummaryByPeriod);

    // ========================================================================
    // PASO 3: HOJA "Clientes/Proveedores" - Ranking con métricas
    // ========================================================================
    const entitiesSorted = [...tableData].sort((a, b) => (b.total || 0) - (a.total || 0));
    // Solo en reporte proveedores el server envía nominasLabel en cada fila
    const showNominasLabel = tableData.some((r) => Object.prototype.hasOwnProperty.call(r || {}, 'nominasLabel'));
    
    const clientesData = entitiesSorted.map((row, index) => {
      const docs = documentsByEntity[row.issuerName] || [];
      const numDocs = docs.length || 1;
      const totalDocumentos = tableData.reduce((sum, r) => {
        const d = documentsByEntity[r.issuerName] || [];
        return sum + (d.length || 1);
      }, 0);
      
      const rowData = {
        [xl.ranking]: index + 1,
        [entityLabel]: row.issuerName || '',
        ...(showNominasLabel
          ? { [xl.label]: formatNominasLabelForExcel(row.nominasLabel) }
          : {}),
        [xl.totalAmount]: row.total || 0,
        [xl.documents]: numDocs,
        [xl.current]: row.vigente || '',
        '': ''
      };
      
      if (index === 0) {
        rowData[xl.totalDebt] = totalDeuda;
        rowData[xl.totalDocs] = totalDocumentos;
        rowData[xl.totalCurrent] = deudaVigente;
      }
      
      return rowData;
    });
    
    const wsClientes = XLSX.utils.json_to_sheet(clientesData);
    wsClientes['!cols'] = showNominasLabel
      ? [
          { wch: 8 },  // Ranking
          { wch: 45 }, // Entidad
          { wch: 22 }, // Etiqueta
          { wch: 16 }, // Deuda Total
          { wch: 12 }, // Documentos
          { wch: 14 }, // Vigente
          { wch: 3 },  // Separador
          { wch: 16 }, // TOTAL DEUDA
          { wch: 12 }, // TOTAL DOCS
          { wch: 14 }  // TOTAL VIGENTE
        ]
      : [
          { wch: 8 },  // Ranking
          { wch: 45 }, // Entidad
          { wch: 16 }, // Deuda Total
          { wch: 12 }, // Documentos
          { wch: 14 }, // Vigente
          { wch: 3 },  // Separador
          { wch: 16 }, // TOTAL DEUDA
          { wch: 12 }, // TOTAL DOCS
          { wch: 14 }  // TOTAL VIGENTE
        ];
    
    // Aplicar formatos
    const rangeCli = XLSX.utils.decode_range(wsClientes['!ref'] || 'A1');
    const moneyCols = showNominasLabel ? [3, 5, 7, 8] : [2, 4, 6, 7];
    for (let R = rangeCli.s.r + 1; R <= rangeCli.e.r; ++R) {
      moneyCols.forEach(c => {
        const cell = wsClientes[XLSX.utils.encode_cell({ r: R, c })];
        if (cell && typeof cell.v === 'number') cell.z = '#,##0';
      });
    }
    
    // Autofilter solo para columnas de datos
    const filterEndCol = showNominasLabel ? 'F' : 'E';
    wsClientes['!autofilter'] = { ref: `A1:${filterEndCol}${clientesData.length + 1}` };
    // Nombre de la hoja: "proveedor" en minúsculas cuando es Proveedor, "clientes" cuando es Cliente
    const sheetName = entityLabel === 'Proveedor' ? 'proveedor' : (entityLabel.toLowerCase() + 's');
    XLSX.utils.book_append_sheet(workbook, wsClientes, sheetName);

    // ========================================================================
    // PASO 3: HOJA "Detalle por Período" - Formato plano para análisis
    // ========================================================================
    const detallePeriodoData = [];
    
    tableData.forEach(row => {
      const entityName = row.issuerName || '';
      const etiqueta = showNominasLabel ? formatNominasLabelForExcel(row.nominasLabel) : null;
      
      xl.agingBuckets.forEach(({ periodo, key }) => {
        const monto = row[key];
        if (monto > 0) {
          detallePeriodoData.push({
            [entityLabel]: entityName,
            ...(showNominasLabel ? { [xl.label]: etiqueta } : {}),
            [xl.period]: periodo,
            [xl.amount]: monto
          });
        }
      });
    });
    
    const wsDetallePeriodo = XLSX.utils.json_to_sheet(detallePeriodoData);
    wsDetallePeriodo['!cols'] = showNominasLabel
      ? [
          { wch: 45 }, // Entidad
          { wch: 22 }, // Etiqueta
          { wch: 15 }, // Período
          { wch: 18 }  // Monto
        ]
      : [
          { wch: 45 }, // Entidad
          { wch: 15 }, // Período
          { wch: 18 }  // Monto
        ];
    
    // Formato moneda
    const rangeDP = XLSX.utils.decode_range(wsDetallePeriodo['!ref'] || 'A1');
    const montoColDP = showNominasLabel ? 3 : 2;
    for (let R = rangeDP.s.r + 1; R <= rangeDP.e.r; ++R) {
      const montoCell = wsDetallePeriodo[XLSX.utils.encode_cell({ r: R, c: montoColDP })];
      if (montoCell && typeof montoCell.v === 'number') montoCell.z = '#,##0';
    }
    
    wsDetallePeriodo['!autofilter'] = { ref: wsDetallePeriodo['!ref'] };
    XLSX.utils.book_append_sheet(workbook, wsDetallePeriodo, xl.sheetDetailByPeriod);

    // ========================================================================
    // PASO 4: HOJA "Detalle" - Anexo técnico con filtros
    // ========================================================================
    const detalleData = [];
    
    tableData.forEach(row => {
      const entityName = row.issuerName || '';
      const etiqueta = showNominasLabel ? formatNominasLabelForExcel(row.nominasLabel) : null;
      const docs = documentsByEntity[entityName] || [];
      
      if (docs.length > 0) {
        docs.forEach(doc => {
          detalleData.push({
            [entityLabel]: entityName,
            ...(showNominasLabel ? { [xl.label]: etiqueta } : {}),
            [xl.folio]: doc.documentId || doc.folio || '',
            [xl.period]: xl.translatePeriod(doc.periodoAtraso || 'Vigente'),
            [xl.balance]: Number(doc.remaining || 0)
          });
        });
      } else {
        detalleData.push({
          [entityLabel]: entityName,
          ...(showNominasLabel ? { [xl.label]: etiqueta } : {}),
          [xl.folio]: '',
          [xl.period]: '',
          [xl.balance]: row.total || 0
        });
      }
    });
    
    const wsDetalle = XLSX.utils.json_to_sheet(detalleData);
    wsDetalle['!cols'] = showNominasLabel
      ? [
          { wch: 45 }, // Entidad
          { wch: 22 }, // Etiqueta
          { wch: 12 }, // Folio
          { wch: 14 }, // Período
          { wch: 18 }  // Saldo
        ]
      : [
          { wch: 45 }, // Entidad
          { wch: 12 }, // Folio
          { wch: 14 }, // Período
          { wch: 18 }  // Saldo
        ];
    
    // Formato moneda
    const rangeDet = XLSX.utils.decode_range(wsDetalle['!ref'] || 'A1');
    const saldoCol = showNominasLabel ? 4 : 3;
    for (let R = rangeDet.s.r + 1; R <= rangeDet.e.r; ++R) {
      const saldoCell = wsDetalle[XLSX.utils.encode_cell({ r: R, c: saldoCol })];
      if (saldoCell && typeof saldoCell.v === 'number') saldoCell.z = '#,##0';
    }
    
    wsDetalle['!autofilter'] = { ref: wsDetalle['!ref'] };
    XLSX.utils.book_append_sheet(workbook, wsDetalle, xl.sheetDetail);

    // ========================================================================
    // REORDENAR HOJAS
    // ========================================================================
    // Nombre de la hoja: "proveedor" en minúsculas cuando es Proveedor, "clientes" cuando es Cliente
    const sheetNameForReorder = entityLabel === 'Proveedor' ? 'proveedor' : (entityLabel.toLowerCase() + 's');
    workbook.SheetNames = [xl.sheetSummaryByPeriod, sheetNameForReorder, xl.sheetDetailByPeriod, xl.sheetDetail];

    // Descargar archivo
    XLSX.writeFile(workbook, fileName);
    
    return true;
  } catch (error) {
    console.error('Error al exportar a Excel:', error);
    return false;
  }
};

export default exportToExcel;

